import { useCallback, useEffect, useMemo } from "react";

import { useMsal } from "@azure/msal-react";
import { Dashboard, Typography } from "components";
import { Authentication as AuthenticationTemplate } from "components/templates/Authentication";
import { AppLanguages, QueryClientGlobal, UseLoginLocal } from "configs";
import { confirm, alert } from "contexts";
import { useAuth } from "hooks/useAuth";
import usePermission from "hooks/usePermission";
import { NotFoundPage } from "pages";
import { useTranslation } from "react-i18next";
import { Route, Routes } from "react-router-dom";
import getSidebarMenus from "routes/sidebar";
import { authLogoutService } from "services";
import { useAppDispatch, useAppSelector } from "stores";
import { authSlice } from "stores/Auth";
import { systemSlice } from "stores/System";
import { saveLocalStorage } from "utils";

import {
  AuthenticationRoutes,
  AuthenticationSuspenseWrapper,
} from "./authentication";
import { DashboardRoutes, DashboardSuspenseWrapper } from "./dashboard";
import { RequireAuth, ProtectedAuth } from "./private";

const AppRoutes = () => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const { isInitializing, isLoggedIn, data, isEmployee, organizations } =
    useAuth();
  const { instance } = useMsal();

  const { canAccess } = usePermission();
  const { language } = useAppSelector((state) => state.system);

  useEffect(
    function checkUserOrganization() {
      if (!isLoggedIn || organizations?.length) return;
      confirm({
        title: "Oops! Something went wrong",
        content: (
          <Typography type="TextSM">
            You do not have permission to access this application.
            <br />
            Please try again later or contact support if the issue persists.
          </Typography>
        ),
        okLabel: "Reload",
        disableCloseOnBackdropAndEscape: true,
      })
        .then(() => {
          window.location.reload();
        })
        .catch(() => {
          window.location.reload();
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoggedIn]
  );

  const sideBarMenus = useMemo(() => {
    const menus = getSidebarMenus({ t });
    const menuWithPermission: typeof menus = [];
    menus.forEach((menu) => {
      if (menu?.onlyAdmin && isEmployee) {
        return;
      }
      if (menu.permission && canAccess(menu.permission) && !menu.children) {
        menuWithPermission.push(menu);
      }
      if (
        menu.children &&
        menu.permissionList.some((item) => canAccess(item))
      ) {
        const parentMenu = { ...menu, children: [] as typeof menu.children };
        menu.children.forEach((child) => {
          if (child.permission && canAccess(child.permission)) {
            parentMenu.children.push(child);
          }
        });
        menuWithPermission.push(parentMenu);
      }
    });
    return menuWithPermission;
  }, [canAccess, isEmployee, t]);

  const handleLogout = useCallback(() => {
    confirm({ title: t("logout"), content: t("doYouWantToLogout") })
      .then(() => {
        authLogoutService()
          .then(() => {
            QueryClientGlobal?.clear();
            dispatch(authSlice.actions.logout());
            dispatch(systemSlice.actions.reset());
            if (UseLoginLocal) {
              const allAccounts = instance.getAllAccounts();

              if (allAccounts?.length) {
                instance?.logoutPopup();
              }
            }
          })
          .catch(() => {
            alert({
              title: t("logoutFailed"),
              content: t("pleaseTryAgain"),
            })
              .then(() => {})
              .catch(() => {});
          });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLanguageChange = useCallback((value: string) => {
    i18n.changeLanguage(value);
    dispatch(systemSlice.actions.changeLanguage(value));
    saveLocalStorage("lan", value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isInitializing) return null;

  return (
    <Routes>
      <Route
        element={
          <RequireAuth>
            <Dashboard
              menu={sideBarMenus}
              bottomMenu={[]}
              userInfo={
                isLoggedIn && data
                  ? { email: data.email, name: data.fullName }
                  : undefined
              }
              languages={AppLanguages}
              defaultLanguage={language}
              onLogout={handleLogout}
              onLanguageChange={handleLanguageChange}
            />
          </RequireAuth>
        }
      >
        {DashboardRoutes.map(
          ({ useSuspense, element, permission, ...innerProps }) => (
            <Route
              key={innerProps.path}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...innerProps}
              element={
                // eslint-disable-next-line no-nested-ternary
                permission && canAccess(permission) ? (
                  useSuspense ? (
                    <DashboardSuspenseWrapper key={innerProps.path}>
                      {element}
                    </DashboardSuspenseWrapper>
                  ) : (
                    element
                  )
                ) : null
              }
            />
          )
        )}
      </Route>
      {UseLoginLocal && (
        <Route
          element={
            <ProtectedAuth>
              <AuthenticationTemplate />
            </ProtectedAuth>
          }
        >
          {AuthenticationRoutes.map(
            ({ useSuspense, element, ...innerProps }) => (
              <Route
                key={innerProps.path}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...innerProps}
                element={
                  useSuspense ? (
                    <AuthenticationSuspenseWrapper key={innerProps.path}>
                      {element}
                    </AuthenticationSuspenseWrapper>
                  ) : (
                    element
                  )
                }
              />
            )
          )}
        </Route>
      )}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default AppRoutes;
